'use client';

import { useEffect, useRef, useState } from 'react';

import { authClient } from '@/lib/auth';
import { initDatadog, setDatadogUser } from '@/lib/datadog';
import { logger } from '@/lib/logger';

export function DatadogInit() {
  const { data: session } = authClient.useSession();
  const [isInitialized, setIsInitialized] = useState(false);
  const identifiedUserId = useRef<string | null>(null);

  // Initialize Datadog once
  useEffect(() => {
    // Skip if not in browser
    if (typeof window === 'undefined') {
      return;
    }

    try {
      initDatadog();
      setIsInitialized(true);
    } catch (error) {
      console.error('[Datadog RUM] Failed to initialize:', error);
      logger.captureException(error as Error, {
        tags: { feature: 'datadog-rum', action: 'init' },
      });
    }
  }, []);

  // Identify user in Datadog when session is available
  useEffect(() => {
    if (!isInitialized || !session?.user) {
      return;
    }

    const { id, email, name } = session.user;

    // Skip if this user is already identified (e.g. auth refetch on tab focus)
    if (identifiedUserId.current === id) {
      return;
    }

    try {
      setDatadogUser({
        id,
        email: email || undefined,
        name: name || undefined,
      });

      identifiedUserId.current = id;
      console.log('[Datadog RUM] User identified', { userId: id, email });
    } catch (error) {
      logger.captureException(error as Error, {
        context: { userId: id, email },
        tags: { feature: 'datadog-rum', action: 'identify' },
      });
    }
  }, [session?.user, isInitialized]);

  // This component doesn't render anything
  return null;
}

export default DatadogInit;
